import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Rarity, SeasonEligibility } from '__generated__/globalTypes';
import { FilterInDropdown } from 'atoms/dropdowns/FilterInDropdown';
import { RadioGroup } from 'atoms/inputs/RadioGroup';
import { FullWidthContainer } from 'atoms/layout/FullWidthContainer';
import { Vertical } from 'atoms/layout/flex';
import { HeadlineS } from 'atoms/typography';
import { seasonEligibilityMessages } from 'lib/glossary';
import { scarcityMessages } from 'lib/scarcity';

const filterableRarities = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
] as const;

type FilterableRarity = (typeof filterableRarities)[number];

type Props = {
  rarity?: Rarity;
  tokenHistory: (
    rarity: Rarity,
    seasonEligibility?: SeasonEligibility
  ) => ReactNode;
  seasonEligibility?: SeasonEligibility;
  withSeasonEligibility?: boolean;
};

export const LastSales = ({
  tokenHistory,
  rarity: rarityProps,
  seasonEligibility: seasonEligibilityProps,
  withSeasonEligibility,
}: Props) => {
  const { formatMessage } = useIntl();
  const [rarityState, setRarityState] = useState<FilterableRarity>(
    Rarity.limited
  );

  const [seasonEligibilityState, setSeasonEligibilityState] =
    useState<SeasonEligibility>(SeasonEligibility.IN_SEASON);

  const rarity = rarityProps || rarityState;
  const seasonEligibility = seasonEligibilityProps || seasonEligibilityState;

  return (
    <FullWidthContainer>
      <Vertical gap={1.5}>
        <HeadlineS as="h2">
          <FormattedMessage
            id="Seo.Player.LastSales.lastSales"
            defaultMessage="Last sales"
          />
        </HeadlineS>
        {!seasonEligibilityProps && withSeasonEligibility && (
          <FilterInDropdown
            buttonLabel={
              <FormattedMessage
                {...seasonEligibilityMessages[seasonEligibility]}
              />
            }
          >
            {({ closeDropdown }) => (
              <RadioGroup
                options={[
                  SeasonEligibility.IN_SEASON,
                  SeasonEligibility.CLASSIC,
                ].map(opt => ({
                  label: formatMessage(seasonEligibilityMessages[opt]),
                  value: opt,
                }))}
                initiallySelectedValue={seasonEligibility}
                name="player-last-sales-rarity"
                onChange={value => {
                  setSeasonEligibilityState(value);
                  closeDropdown();
                }}
              />
            )}
          </FilterInDropdown>
        )}
        {!rarityProps && (
          <FilterInDropdown
            buttonLabel={
              <FormattedMessage {...scarcityMessages[rarityState]} />
            }
          >
            {({ closeDropdown }) => (
              <RadioGroup
                options={[...filterableRarities].map(filterableRarity => ({
                  label: formatMessage(scarcityMessages[filterableRarity]),
                  value: filterableRarity,
                }))}
                initiallySelectedValue={rarityState}
                name="player-last-sales-rarity"
                onChange={value => {
                  setRarityState(value);
                  closeDropdown();
                }}
              />
            )}
          </FilterInDropdown>
        )}
        {tokenHistory(
          rarity,
          (withSeasonEligibility && seasonEligibility) || undefined
        )}
      </Vertical>
    </FullWidthContainer>
  );
};
